import React, { useCallback, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { partial, uniqueId } from 'lodash';

import * as timecardActions from 'features/timesheets/redux/timecardSlice';
import {
  editTimecard,
  mergeShifts,
} from 'features/timesheets/TimesheetsPage/TimecardModal/slices';

import Icon from 'components/Icon';

import { toI18n } from 'util/i18n';
import { trackUxEvent } from 'util/tracking';
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
} from 'util/tracking_constants';

import { NO_SHOW_REASONS_LIST } from '../../../constants';
import AddNote from '../../../NoteSection/AddNote.jsx';

import Issue from './Issue';

const titleIcon = <Icon type="chevronLeft" size="xs" />;
const addNotePlaceholder = toI18n(
  'timecard_modal_v3.issues.no_show.resolve.note_placeholder'
);

const NoShow = ({
  addTimebreak,
  issue,
  onChangeShift,
  shift,
  onMergeShifts,
  fixDuplicate,
  setChangesMade,
}) => {
  const dispatch = useDispatch();
  const [currentMenu, setCurrentMenu] = useState('initial');
  const [selectedNoShowReason, setSelectedNoShowReason] = useState(null);

  const currentTitle = useMemo(() => {
    switch (currentMenu) {
      case 'initial':
        return toI18n('timecard_modal_v3.issues.no_show.resolve.title');
      case 'add_reason':
        return toI18n('timecard_modal_v3.issues.no_show.resolve.choose_reason');
      case 'notes':
        return `${toI18n(
          'timecard_modal_v3.issues.no_show.content'
        )}: ${selectedNoShowReason}`;
      case 'unscheduled_shift':
        return toI18n(
          'timecard_modal_v3.issues.no_show.resolve.unscheduled_shifts_title'
        );
      default:
        return '';
    }
  }, [currentMenu, selectedNoShowReason]);

  const handleAddScheduled = useCallback(() => {
    setChangesMade(true);
    trackUxEvent({
      productArea: PRODUCT_AREAS.TIMESHEETS,
      eventCategory: EVENT_CATEGORIES.TIMECARD_DETAIL,
      eventAction: EVENT_ACTIONS.ADD_HOURS_AND_BREAKS_AS_SCHEDULED_CLICKED,
    });
    dispatch(editTimecard());
    onChangeShift('actual_start_at', shift.get('scheduled_start_at'));
    onChangeShift('actual_end_at', shift.get('scheduled_end_at'));
    issue
      .get('meta')
      .get('scheduled_breaks')
      .forEach(scheduledBreak =>
        addTimebreak({
          key: uniqueId('NEW-TIMEBREAK'),
          start_at: scheduledBreak.get('start_at'),
          end_at: scheduledBreak.get('end_at'),
          mandated_break_id: scheduledBreak.get('mandated_break_id'),
          paid: scheduledBreak.get('paid'),
        })
      );
  }, [issue, addTimebreak, dispatch, onChangeShift, setChangesMade, shift]);

  function handleClickAddReason() {
    trackUxEvent({
      productArea: PRODUCT_AREAS.TIMESHEETS,
      eventCategory: EVENT_CATEGORIES.TIMECARD_DETAIL,
      eventAction: EVENT_ACTIONS.ADD_REASON_FOR_A_NO_SHOW_CLICKED_V3,
    });
    setCurrentMenu('add_reason');
  }

  function handleClickMakeEdit() {
    setChangesMade(true);
    trackUxEvent({
      productArea: PRODUCT_AREAS.TIMESHEETS,
      eventCategory: EVENT_CATEGORIES.TIMECARD_DETAIL,
      eventAction: EVENT_ACTIONS.MAKE_A_DIFFERENT_EDIT_CLICKED_V3,
    });
    dispatch(editTimecard());
  }

  const handleMergeShifts = useCallback(
    unscheduledShiftId => {
      onMergeShifts({
        scheduledShiftId: shift.get('id'),
        unscheduledShiftId,
      }).then(() => {
        const originPath = window.location.pathname;
        fixDuplicate(shift.get('id'), unscheduledShiftId, originPath);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onMergeShifts, fixDuplicate, shift.get('id')]
  );

  const handleClickTitle = useCallback(() => {
    if (currentMenu === 'notes') {
      setCurrentMenu('add_reason');
      return;
    }
    setCurrentMenu('initial');
  }, [setCurrentMenu, currentMenu]);

  const handleClickReason = useCallback(
    reason => {
      setSelectedNoShowReason(reason);
      setCurrentMenu('notes');
    },
    [setSelectedNoShowReason, setCurrentMenu]
  );

  function options() {
    switch (currentMenu) {
      case 'initial': {
        const initialOptions = [
          {
            label: toI18n(
              'timecard_modal_v3.issues.no_show.resolve.add_hours_and_breaks'
            ),
            color: 'navy',
            paddingLeft: 18,
            onClick: handleAddScheduled,
          },
        ];

        if (issue.getIn(['meta', 'unscheduled_shifts']).size) {
          initialOptions.push({
            label: toI18n(
              'timecard_modal_v3.issues.no_show.resolve.combine_with_unscheduled'
            ),
            onClick: partial(setCurrentMenu, 'unscheduled_shift'),
            color: 'navy',
            paddingLeft: 18,
            doNotClose: true,
          });
        }

        initialOptions.push({
          label: toI18n('timecard_modal_v3.issues.no_show.resolve.add_reason'),
          onClick: handleClickAddReason,
          color: 'navy',
          paddingLeft: 18,
          doNotClose: true,
        });

        initialOptions.push({
          label: toI18n('timecard_modal_v3.issues.no_show.resolve.make_edit'),
          onClick: handleClickMakeEdit,
          color: 'navy',
          paddingLeft: 18,
        });

        return initialOptions;
      }
      case 'add_reason':
        return NO_SHOW_REASONS_LIST.map(reason => ({
          label: reason,
          color: 'navy',
          paddingLeft: 18,
          onClick: partial(handleClickReason, reason),
          doNotClose: true,
        }));
      case 'unscheduled_shift':
        return issue
          .getIn(['meta', 'unscheduled_shifts'])
          .map(unscheduledShift => ({
            label: unscheduledShift.get('label'),
            color: 'navy',
            paddingLeft: 18,
            onClick: partial(handleMergeShifts, unscheduledShift.get('id')),
            doNotClose: true,
          }));
      default:
        return [];
    }
  }

  return (
    <Issue
      issue={issue}
      options={options()}
      body={
        currentMenu === 'notes' && (
          <AddNote
            shiftId={shift.get('id')}
            placeholder={addNotePlaceholder}
            noShowReason={selectedNoShowReason}
          />
        )
      }
      title={currentTitle}
      titleIcon={
        ['add_reason', 'notes', 'unscheduled_shift'].includes(currentMenu) &&
        titleIcon
      }
      onClickTitle={
        ['add_reason', 'notes', 'unscheduled_shift'].includes(currentMenu) &&
        handleClickTitle
      }
    />
  );
};

export default connect(() => ({}), {
  onMergeShifts: mergeShifts,
  fixDuplicate: timecardActions.fixDuplicate,
})(NoShow);
